import { PIECES } from "../engine/pieces";
import { PIECE_TYPES, type Category, type Faction, type PieceType } from "../engine/types";
import { categoryLabel, pieceLabel, type Language, type MessageKey } from "../i18n/catalog";
import { FactionPieceImage } from "../components/FactionPieceImage";

const TARGET_ASSET: Record<Category, string> = {
  INFANTRY: "codex-ui/category-infantry.png",
  WHEELED: "codex-ui/category-wheeled.png",
  ARMORED: "codex-ui/category-armored.png",
  AIR: "codex-ui/category-air.png",
  BUILDING: "codex-ui/category-building.png",
};

const tr = (language: Language, cs: string, en: string, de: string) =>
  language === "cs" ? cs : language === "de" ? de : en;

function OfficialStat({ asset, value, label }: { asset: string; value: number; label: string }) {
  return <span title={label}><img src={`${import.meta.env.BASE_URL}assets/${asset}`} alt={label}/><b>{value}</b></span>;
}

function Stats({ type, language }: { type: PieceType; language: Language }) {
  const piece = PIECES[type];
  return <div className="codex-official-stats">
    <OfficialStat asset="stat_production_cost.png" value={piece.cost} label={tr(language, "Cena výroby", "Production cost", "Produktionskosten")}/>
    <OfficialStat asset="stat_production_time.png" value={piece.productionTime} label={tr(language, "Doba výroby", "Production time", "Produktionszeit")}/>
    <OfficialStat asset="stat_tech_level.png" value={piece.tech} label={tr(language, "Technologická úroveň", "Tech level", "Tech-Stufe")}/>
  </div>;
}

function Targets({ type, language }: { type: PieceType; language: Language }) {
  const targets = PIECES[type].targets;
  return <div className="codex-targets"><small>{tr(language, "CÍLOVÉ FIGURKY", "TARGET FIGURES", "ZIELFIGUREN")}</small>
    <div className={`target-icon-list ${targets.length >= 5 ? "many-targets" : ""}`}>{targets.length ? targets.map(target => <figure title={categoryLabel(target, language)} key={target}>
      <img src={`${import.meta.env.BASE_URL}assets/${TARGET_ASSET[target]}`} alt={categoryLabel(target, language)}/>
      <figcaption>{categoryLabel(target, language)}</figcaption>
    </figure>) : <span className="no-targets">—</span>}</div>
  </div>;
}

export function CodexCard({ type, language, compact = false, owner = "EFA" }: { type: PieceType; language: Language; compact?: boolean; owner?: Faction }) {
  return <article className={`codex-card-v2 codex-owner-${owner.toLowerCase()} ${compact ? "codex-card-quick" : ""}`}>
    <strong className="codex-card-name">{pieceLabel(type, language)}</strong>
    <em>{categoryLabel(PIECES[type].category, language)}</em>
    <FactionPieceImage className="codex-piece codex-piece-canvas" asset={PIECES[type].asset} faction={owner} ariaLabel={pieceLabel(type, language)}/>
    <Stats type={type} language={language}/>
    <Targets type={type} language={language}/>
    <div className="codex-secondary-stats compact">
      <span><small>{tr(language, "MAX.", "MAX", "MAX.")}</small><b>{PIECES[type].maxCount}</b></span>
      <span><small>{tr(language, "POHYB", "SPEED", "BEWEGUNG")}</small><b>{PIECES[type].speed}</b></span>
      <span><small>{tr(language, "DOSAH", "RANGE", "REICHWEITE")}</small><b>{PIECES[type].minRange}–{PIECES[type].maxRange}</b></span>
    </div>
  </article>;
}

export function CodexScreen({ language, t }: { language: Language; t: (key: MessageKey) => string }) {
  const groups = [
    ["BUILDING", PIECE_TYPES.filter(type => PIECES[type].category === "BUILDING")],
    ["INFANTRY", PIECE_TYPES.filter(type => PIECES[type].category === "INFANTRY")],
    ["AIR", PIECE_TYPES.filter(type => PIECES[type].category === "AIR")],
    ["ARMORED", PIECE_TYPES.filter(type => ["WHEELED", "ARMORED"].includes(PIECES[type].category))],
  ] as const;
  return <section className="codex-screen-v2">
    <div className="section-title"><span/><h2>{t("rules")}</h2><span/></div>
    <div className="codex-groups">{groups.map(([category, types]) => <section className={`codex-category codex-category-${category.toLowerCase()}`} key={category}><h4>{categoryLabel(category, language)}</h4><div className="codex-grid codex-grid-v2">{types.map(type => <CodexCard type={type} language={language} key={type}/>)}</div></section>)}</div>
  </section>;
}
